import { uploadData, getUrl, remove, list } from 'aws-amplify/storage';
import { Receipt } from '../types';
import { v4 as uuidv4 } from 'uuid';

/**
 * Storage service for handling file uploads to S3
 * Requirements: 2.2, 2.5 - Receipt upload and validation
 */
export class StorageService {
  private static readonly RECEIPT_FOLDER = 'receipts/';
  private static readonly ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
  private static readonly MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  /**
   * Validates file before upload
   * Requirements: 2.5 - File type validation
   */
  static validateFile(file: File): { isValid: boolean; error?: string } {
    // Check file type
    if (!this.ALLOWED_TYPES.includes(file.type)) {
      return {
        isValid: false,
        error: 'File type not supported. Please upload PDF, JPG, or PNG files only.'
      };
    }

    // Check file size
    if (file.size > this.MAX_FILE_SIZE) {
      const maxSizeMB = this.MAX_FILE_SIZE / (1024 * 1024);
      return {
        isValid: false,
        error: `File size exceeds ${maxSizeMB}MB limit.`
      };
    }

    // Check file extension matches MIME type
    const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    const validExtensionForType = this.getValidExtensionsForType(file.type);
    
    if (!validExtensionForType.includes(extension)) {
      return {
        isValid: false,
        error: `File extension ${extension} does not match file type ${file.type}. Please ensure the file extension matches the file type.`
      };
    }

    return { isValid: true };
  }

  /**
   * Uploads a receipt file to S3
   * Requirements: 2.2 - Receipt upload with S3 integration
   */
  static async uploadReceipt(
    expenseId: string, 
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<Receipt> {
    // Validate file first
    const validation = this.validateFile(file);
    if (!validation.isValid) {
      throw new Error(validation.error);
    }

    try {
      // Generate unique file name
      const fileExtension = file.name.substring(file.name.lastIndexOf('.'));
      const uniqueFileName = `${expenseId}_${uuidv4()}${fileExtension}`;
      const s3Key = `${this.RECEIPT_FOLDER}${uniqueFileName}`;

      // Upload to S3 with progress tracking using v6 API
      await uploadData({
        key: s3Key,
        data: file,
        options: {
          contentType: file.type,
          onProgress: (progress) => {
            if (progress.totalBytes) {
              const percentage = Math.round((progress.transferredBytes / progress.totalBytes) * 100);
              onProgress?.(percentage);
            }
          },
          metadata: {
            expenseId,
            originalFileName: file.name,
            uploadedAt: new Date().toISOString()
          }
        }
      }).result;

      // Get the file URL
      const urlResult = await getUrl({ key: s3Key });

      // Create receipt object
      const receipt: Receipt = {
        id: uuidv4(),
        expenseId,
        fileName: file.name,
        fileUrl: urlResult.url.toString(),
        fileKey: s3Key,
        fileType: this.normalizeFileType(file.type),
        fileSize: file.size,
        uploadedAt: new Date().toISOString()
      };

      return receipt;

    } catch (error) {
      console.error('Receipt upload failed:', error);
      throw new Error(
        error instanceof Error 
          ? `Upload failed: ${error.message}` 
          : 'Upload failed due to an unknown error'
      );
    }
  }

  /**
   * Deletes a receipt file from S3
   * Requirements: 2.2 - Receipt management
   */
  static async deleteReceipt(receipt: Receipt): Promise<void> {
    try {
      // Extract S3 key from file URL or construct it
      const s3Key = this.extractS3KeyFromUrl(receipt.fileUrl) || 
                   `${this.RECEIPT_FOLDER}${receipt.fileName}`;

      await remove({ key: s3Key });
    } catch (error) {
      console.error('Receipt deletion failed:', error);
      throw new Error(
        error instanceof Error 
          ? `Delete failed: ${error.message}` 
          : 'Delete failed due to an unknown error'
      );
    }
  }

  /**
   * Gets a pre-signed URL for file access
   * Requirements: 2.2 - Secure file access
   * Note: Amplify v6 automatically adds 'public/' prefix, so we need to handle this
   */
  static async getReceiptUrl(s3Key: string, expiresIn: number = 3600): Promise<string> {
    try {
      // Remove 'public/' prefix if present - Amplify adds it automatically
      let cleanKey = s3Key;
      if (cleanKey.startsWith('public/')) {
        cleanKey = cleanKey.substring(7); // Remove 'public/'
      }
      
      console.log('[Storage] Getting URL for key:', cleanKey);
      
      const urlResult = await getUrl({ 
        key: cleanKey,
        options: { expiresIn }
      });
      
      console.log('[Storage] Generated URL:', urlResult.url.toString().substring(0, 100) + '...');
      return urlResult.url.toString();
    } catch (error) {
      console.error('Failed to get receipt URL:', error);
      throw new Error('Failed to generate file access URL');
    }
  }

  /**
   * Generates a pre-signed URL for direct upload
   * Requirements: 2.2 - Pre-signed URL generation
   */
  static async generatePresignedUploadUrl(
    expenseId: string,
    fileName: string,
    fileType: string
  ): Promise<{ uploadUrl: string; s3Key: string }> {
    try {
      // Validate file type
      if (!this.ALLOWED_TYPES.includes(fileType)) {
        throw new Error('File type not supported');
      }

      // Generate unique file name
      const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
      const uniqueFileName = `${expenseId}_${uuidv4()}${fileExtension}`;
      const s3Key = `${this.RECEIPT_FOLDER}${uniqueFileName}`;

      // Generate pre-signed URL
      const urlResult = await getUrl({ 
        key: s3Key,
        options: { expiresIn: 3600 }
      });

      return {
        uploadUrl: urlResult.url.toString(),
        s3Key
      };
    } catch (error) {
      console.error('Failed to generate pre-signed URL:', error);
      throw new Error('Failed to generate upload URL');
    }
  }

  /**
   * Lists all receipts for an expense from S3 directly
   * Fallback when DynamoDB records are missing but files exist in S3
   */
  static async listReceiptsFromS3(expenseId: string): Promise<Receipt[]> {
    try {
      const prefix = `${this.RECEIPT_FOLDER}${expenseId}_`;
      console.log('[Storage] Listing S3 objects with prefix:', prefix);
      
      let items: any[] = [];
      
      // Try key-based list first (Amplify Gen 1 / v6 with S3 config)
      // Amplify auto-prepends 'public/' to the key
      try {
        const result = await list({ 
          prefix: prefix,
          options: { listAll: true }
        } as any);
        items = result.items || (result as any).results || [];
        console.log('[Storage] Key-based list returned', items.length, 'items');
      } catch (keyErr) {
        console.log('[Storage] Key-based list failed, trying path-based...');
        // Fallback: path-based list (Amplify Gen 2 style)
        try {
          const result = await list({ 
            path: `public/${prefix}`,
            options: { listAll: true }
          } as any);
          items = result.items || [];
          console.log('[Storage] Path-based list returned', items.length, 'items');
        } catch (pathErr) {
          console.warn('[Storage] Both list approaches failed');
          return [];
        }
      }
      
      if (items.length === 0) return [];
      
      const receipts: Receipt[] = [];
      for (const item of items) {
        try {
          // item may have 'key' (Gen 1) or 'path' (Gen 2)
          const rawKey = item.key || item.path || '';
          // Remove 'public/' prefix if present — Amplify getUrl adds it back
          const s3Key = rawKey.startsWith('public/') ? rawKey.substring(7) : rawKey;
          const fileName = s3Key.split('/').pop() || s3Key;
          
          // Skip if empty or is a folder marker
          if (!fileName || fileName.endsWith('/') || (item.size === 0 && !fileName.includes('.'))) continue;
          
          const ext = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
          const fileType = ext === '.pdf' ? 'PDF' : ext === '.png' ? 'PNG' : 'JPG';
          
          const urlResult = await getUrl({ key: s3Key });
          
          receipts.push({
            id: `s3-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
            expenseId,
            fileName,
            fileUrl: urlResult.url.toString(),
            fileKey: s3Key,
            fileType,
            fileSize: item.size || 0,
            uploadedAt: item.lastModified?.toISOString() || new Date().toISOString()
          });
        } catch (itemError) {
          console.warn('[Storage] Error processing S3 item:', item, itemError);
        }
      }
      
      return receipts;
    } catch (error) {
      console.warn('[Storage] S3 list not available (IAM permission needed), skipping fallback');
      return [];
    }
  }

  /**
   * Lists all receipts for an expense
   * Requirements: 2.2 - Receipt listing
   */
  static async listReceiptsForExpense(expenseId: string): Promise<Receipt[]> {
    try {
      // Receipt records are not separately queryable via a dedicated GraphQL
      // API today, so fall back to listing the objects directly from S3
      // (same approach used elsewhere as a fallback when DynamoDB records
      // are missing but the files themselves exist in S3).
      return await this.listReceiptsFromS3(expenseId);
    } catch (error) {
      console.error('Failed to list receipts:', error);
      throw new Error('Failed to retrieve receipts');
    }
  }

  /**
   * Gets valid file extensions for a given MIME type
   */
  private static getValidExtensionsForType(mimeType: string): string[] {
    switch (mimeType) {
      case 'application/pdf':
        return ['.pdf'];
      case 'image/jpeg':
      case 'image/jpg':
        return ['.jpg', '.jpeg'];
      case 'image/png':
        return ['.png'];
      default:
        return [];
    }
  }

  /**
   * Normalizes file type for consistent storage
   */
  private static normalizeFileType(mimeType: string): string {
    switch (mimeType) {
      case 'application/pdf':
        return 'PDF';
      case 'image/jpeg':
      case 'image/jpg':
        return 'JPG';
      case 'image/png':
        return 'PNG';
      default:
        return mimeType.toUpperCase();
    }
  }

  /**
   * Extracts S3 key from a pre-signed URL
   */
  private static extractS3KeyFromUrl(url: string): string | null {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      return pathname.startsWith('/') ? pathname.substring(1) : pathname;
    } catch {
      return null;
    }
  }

  /**
   * Formats file size for display
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Gets file type icon for display
   */
  static getFileTypeIcon(fileType: string): string {
    switch (fileType.toLowerCase()) {
      case 'pdf':
        return '📄';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return '🖼️';
      default:
        return '📎';
    }
  }
}
